import { faCalendar, faLocationDot } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import type { Trip } from '@/types/trip';
import { formatAddress } from '@/utils/address';
import { formatDateRange, getTripState, type TripState } from '@/utils/schedule';
import { type ClassName, twx } from '@/utils/tailwind';

/** Trip-state badge palette — each stage of the planning journey reads as a
 * distinct kawaii accent, shared by every surface that lists trips so the
 * Scouting / Curating / Ready-to-Travel chips stay visually consistent.
 * Each badge carries a 1px border in the tone's "main" shade so it stays
 * visible against any row background — the trip-selector menu, in
 * particular, paints the selected row in `bg-primary-subtle` which would
 * otherwise camouflage the Curating badge. */
const tripStateBadgeClasses: Record<TripState, string> = {
  Scouting: 'bg-secondary-subtle text-secondary-emphasized border border-secondary-main/60',
  Curating: 'bg-primary-subtle text-primary-emphasized border border-primary-main/60',
  'Ready to Travel': 'bg-tertiary-subtle text-tertiary-emphasized border border-tertiary-main/60',
};

export const tripRowBadgePositions = ['left', 'right'] as const;
export type TripRowBadgePosition = (typeof tripRowBadgePositions)[number];

export interface TripRowProps {
  trip: Trip;
  isSelected: boolean;
  onSelect: () => void;
  /** Optional trailing slot for per-row actions (export, share, delete).
   * Rendered outside the selectable area so clicks don't fall through to
   * row selection. Omit for read-only listings (e.g. EmptyState). */
  trailing?: ReactNode;
  /** Where the state badge sits relative to the trip name. `'right'`
   *  (default) suits dense lists with no trailing actions — pushes the
   *  badge to the row's right edge. `'left'` sits the badge immediately
   *  after the title; the trip selector menu uses this because its
   *  trailing action grid already occupies the row's right edge. */
  badgePosition?: TripRowBadgePosition;
  className?: ClassName;
}

/** Compact row showing a trip's name, state badge, date range, place count,
 * and (if known) primary location. Used by the trip-selector dropdown and
 * the welcome screen's trip list — the two surfaces stayed visually in sync
 * by hand-duplication before this primitive existed. */
export const TripRow = ({
  trip,
  isSelected,
  onSelect,
  trailing,
  badgePosition = 'right',
  className,
}: TripRowProps) => {
  const state = getTripState(trip);
  const placeWord = trip.places.length === 1 ? 'place' : 'places';
  const titleClass = twx(
    'min-w-0 text-sm font-medium truncate',
    // When the badge is right-aligned the title takes flex-1 to push
    // the badge to the row's right edge. When the badge is left-aligned
    // we drop flex-1 so the title shrinks to fit and the badge sits
    // adjacent to it instead of separated by a gap.
    badgePosition === 'right' && 'flex-1',
    isSelected ? 'text-primary-focal' : 'text-content-text',
  );
  return (
    <div
      className={twx(
        'flex items-stretch rounded-lg overflow-hidden transition-colors',
        isSelected ? 'bg-primary-subtle' : 'bg-content-focal hover:bg-content-background',
        className,
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 min-w-0 flex flex-col px-3 py-2 text-left"
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={titleClass}>{trip.name}</span>
          <span
            className={twx(
              'shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider transition-colors duration-500',
              tripStateBadgeClasses[state],
            )}
          >
            {state}
          </span>
        </span>
        <span className="text-xs text-content-subtle mt-0.5 truncate flex items-center gap-1">
          <FontAwesomeIcon icon={faCalendar} style={{ width: 9, height: 9 }} className="shrink-0" />
          <span className="truncate">
            {formatDateRange(trip.startDate, trip.endDate)}
            <span className="mx-1 text-base leading-none">·</span>
            {trip.places.length} {placeWord}
          </span>
        </span>
        {trip.location && (
          <span className="text-xs text-content-subtle truncate flex items-center gap-1">
            <FontAwesomeIcon
              icon={faLocationDot}
              style={{ width: 9, height: 9 }}
              className="shrink-0"
            />
            {formatAddress(trip.location)}
          </span>
        )}
      </button>
      {trailing && <div className="shrink-0 self-center pr-1.5">{trailing}</div>}
    </div>
  );
};
